import React, { memo, useCallback, useMemo, useState } from 'react';
import { Checkbox, Range } from './index';
import { AiOutlineDown, AiOutlineUp, AiFillQuestionCircle } from 'react-icons/ai';
import { formatPriceValue, formatMileageValue } from '@utils';
import styles from './Filter.module.scss';
import { translations } from '@utils/translations';
import { filterInfo } from '../../../config/filters';
import { filter } from 'lodash';

/**
 * A collection of arrow components.
 * This object helps out with selecting the proper component
 * based on the direction of the arrow.
 */
const arrowComponents = {
    up: () => <AiOutlineUp />,
    down: () => <AiOutlineDown />
};

/**
 * Just like the arrows, we determine the direction of the
 * arrows based on if the filters should be shown or not.
 * Since objects don't support booleans as keys, we use a Map.
 */
const directionMap = new Map();
directionMap.set(true, 'up');
directionMap.set(false, 'down');

/**
 * Define DataLayer for GTM to use.
 */
window.dataLayer = window.dataLayer || [];

const isQuestionMarkElement = (e) => {
    if (e.target.tagName === 'path') {
        return e.target.parentNode.classList.contains('op-filter__question-mark');
    }

    return e.target.parentNode.classList.contains('op-filter__question-mark');
};

const Filter = memo(({ type, title, options = [], active = true, setIsFilterInfoModalOpen, setCurrentSelectedFilter }) => {
    const [shouldFiltersShow, setShouldFiltersShow] = useState(active);
    const [arrowDirection, setArrowDirection] = useState(
        directionMap.get(active)
    );
    const [numberOfitemsShown, setNumberOfItemsToShown] = useState(8);
    const [isMore, setIsMore] = useState(false);

    const translate = s => translations['nl'][s] || s;

    /**
     * Open and close the filters when clicking the header.
     */
    const toggleFilters = (e) => {
        if (isQuestionMarkElement(e)) {
            return;
        }
        const direction = directionMap.get(!shouldFiltersShow);
        setArrowDirection(direction);
        setShouldFiltersShow(!shouldFiltersShow);
    };

    /**
     * Toggle show more options.
     */
    const toggleShowMoreOrLess = () => {
        if (isMore === false) {
            setNumberOfItemsToShown(options.length);
            setIsMore(true);
        } else {
            setNumberOfItemsToShown(8);
            setIsMore(false);
        }
    };

    /**
     * Get the correct number formatting.
     *
     * @param name
     * @returns {string}
     */
    const labelFormat = useCallback(
        name => {
            if (title === 'PriceOnline') {
                return formatPriceValue(name);
            }

            if (title === 'Mileage') {
                return formatMileageValue(name);
            }

            return name;
        },
        [title]
    );

    const itemsToShow = useMemo(() => {
        return options.slice(0, numberOfitemsShown).map((option, index) => {
            return (
                <Checkbox
                    key={title + index}
                    title={title}
                    isSelected={option.selected}
                    name={option.name}
                    count={option.count}
                    value={option.value}
                />
            );
        });
    }, [options, numberOfitemsShown, title]);

    /**
     * Here we remove all the non-numeric options for the range filter.
     *
     * @type {number[]}
     */
    const rangeOptions = useMemo(() => {
        return options.filter(
            ({ value }) => !isNaN(parseFloat(value)) && isFinite(value)
        );
    }, [options]);

    const selectedOptions = useMemo(
        () => options.filter(({ selected }) => selected === true),
        [options]
    );

    const activeItems = useMemo(
        () => selectedOptions.map(({ name }) => labelFormat(name)).join(', '),
        [selectedOptions, labelFormat]
    );


    /**
     * Select the correct arrow component based on the arrowDirection.
     *
     * @type {JSX.Element}
     */
    const ArrowComponent = arrowComponents[arrowDirection];

    if (!options.length) {
        return null;
    }

    return (
        <div className={`op-filter-item op-filter__${type} ${styles.item}`}>
            <div className={`op-filter__header ${styles.header}`} onClick={toggleFilters}>
                <div className={`op-filter__category`}>
                    <span className={`op-filter__title`}>{translate(title)}</span>
                    {filterInfo[title] !== undefined && (
                        <>
                            <AiFillQuestionCircle className={`op-filter__question-mark`} onClick={(e) => {
                                setIsFilterInfoModalOpen(true);
                                setCurrentSelectedFilter(title);
                                window.dataLayer.push({
                                    event: 'filter-question-mark-click',
                                    filterName: translate(title)
                                });
                            } }/>
                        </>
                    )}
                </div>
                <ArrowComponent/>
            </div>

            {shouldFiltersShow && (
                <div className={`op-filter__options`}>
                    {type === 'checkbox' && (
                        <>
                            <ul className={`op-filter__items`}>
                                {itemsToShow.length && itemsToShow}
                            </ul>

                            {options.length > 8 && (
                                <button
                                    type="button"
                                    className={`op-filter__more`}
                                    onClick={toggleShowMoreOrLess}
                                >
                                    {isMore ? 'Toon minder -' : 'Toon meer +'}
                                </button>
                            )}
                        </>
                    )}
                    {type === 'range' && (
                        <Range title={title} options={rangeOptions} />
                    )}
                </div>
            )}

            {!shouldFiltersShow && (
                <div className={`op-filter__closed`}>
                    {selectedOptions.length === 0 && (
                        <span
                            className={`op-filter__closed-text`}
                            onClick={toggleFilters}
                        >
                            {translate(`Select ${title}`)}
                        </span>
                    )}

                    {selectedOptions.length !== 0 && (
                        <span className={`op-filter__closed-list`}>
                            { title === 'IsAdapted'
                                ? activeItems === 'true' ? 'Ja' : 'Nee'
                                : activeItems
                            }
                        </span>
                    )}
                </div>
            )}
        </div>
    );
});

export default Filter;
